(ns hai.ui.components.date-picker
  "Framework-agnostic views for picking a day and a time of day: the
   [[calendar]] and the [[time-view]]. Pure reagent + Ornament, no re-frame
   and no Radix, so they run in both the web-app and the (reagent-only)
   browser extension.

   A day is `{:year 2026 :month 6 :day 8}`; a time is minutes since midnight.
   Turning them into an absolute instant and timezone is the caller's job
   ([[hai.ui.components.datetime]])."
  {:ornament/prefix "datepicker-"}
  (:require
   [clojure.string :as str]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private previous-icon (icons/mui-icon :outlined :chevron_left))
(def ^:private next-icon (icons/mui-icon :outlined :chevron_right))

;; ---------------------------------------------------------------------------
;; Days (calendar math via native js/Date in the browser's local zone)
;; ---------------------------------------------------------------------------

(def ^:private weekday-names ["Sun" "Mon" "Tue" "Wed" "Thu" "Fri" "Sat"])
(def ^:private weekday-initials ["Mo" "Tu" "We" "Th" "Fr" "Sa" "Su"])
(def ^:private month-names
  ["January" "February" "March" "April" "May" "June"
   "July" "August" "September" "October" "November" "December"])
(def ^:private month-abbrevs
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- ->js-date [{:keys [year month day]}]
  (js/Date. year (dec month) (or day 1)))

(defn- js-date->parts [d]
  {:year (.getFullYear d) :month (inc (.getMonth d)) :day (.getDate d)})

(defn today-date
  "Today as a day map."
  []
  (js-date->parts (js/Date.)))

(defn- same-date? [a b]
  (and a b
       (= (:year a) (:year b))
       (= (:month a) (:month b))
       (= (:day a) (:day b))))

(defn- before? [a b]
  (< (.getTime (->js-date a)) (.getTime (->js-date b))))

(defn- shift-month [{:keys [year month]} delta]
  (let [idx (+ (* year 12) (dec month) delta)]
    {:year (quot idx 12) :month (inc (mod idx 12))}))

(defn- month-cells
  "The month's grid, six weeks from Monday so it always holds one height: the
   month's days as `{:day n}`, padded with the days either side of it as
   `{:day n :adjacent? true}`."
  [{:keys [year month]}]
  (let [leading (mod (+ (.getDay (js/Date. year (dec month) 1)) 6) 7)
        days (.getDate (js/Date. year month 0))
        prev-days (.getDate (js/Date. year (dec month) 0))
        trailing (- 42 leading days)]
    (concat
     (for [d (range (inc (- prev-days leading)) (inc prev-days))]
       {:day d :adjacent? true})
     (for [d (range 1 (inc days))]
       {:day d})
     (for [d (range 1 (inc trailing))]
       {:day d :adjacent? true}))))

(defn day-label
  "A day as a short name, like \"Mon 28 Sep\"."
  [date]
  (str (nth weekday-names (.getDay (->js-date date))) " "
       (:day date) " "
       (nth month-abbrevs (dec (:month date)))))

;; ---------------------------------------------------------------------------
;; Times
;; ---------------------------------------------------------------------------

(def ^:private default-time (* 9 60))

(def ^:private quick-times
  [["Morning" (* 9 60)]
   ["Afternoon" (* 14 60)]
   ["Evening" (* 18 60)]])

(defn- pad2 [n]
  (.padStart (str n) 2 "0"))

(defn format-minutes
  "Minutes since midnight as a 12-hour label, e.g. 1020 -> \"5:00 PM\"."
  [minutes]
  (let [h24 (quot minutes 60)
        am-pm (if (< h24 12) "AM" "PM")
        h12 (let [h (mod h24 12)] (if (zero? h) 12 h))]
    (str h12 ":" (pad2 (mod minutes 60)) " " am-pm)))

(defn- typed-label
  "Minutes since midnight as the time box shows them, like \"9:30\"; the
   AM/PM toggle beside it says which half of the day."
  [minutes]
  (str (let [h (mod (quot minutes 60) 12)] (if (zero? h) 12 h))
       ":" (pad2 (mod minutes 60))))

(defn- typed-parts
  "Hours and minutes read out of what was typed into the time box, or nil.
   `9` is 9:00 and `930` is 9:30; `9:3` is half past. Two digits that cannot be
   an hour are an hour and a ten of minutes — `93` is 9:30, the same as `9:3`."
  [text]
  (let [parse #(js/parseInt % 10)]
    (if (str/includes? text ":")
      (let [[hours minutes] (str/split text #":" 2)
            hour-digits (str/replace hours #"\D" "")
            minute-digits (str/replace (or minutes "") #"\D" "")]
        (when (seq hour-digits)
          [(parse hour-digits)
           (case (count minute-digits)
             0 0
             1 (* 10 (parse minute-digits))
             (parse (subs minute-digits 0 2)))]))
      (let [digits (str/replace text #"\D" "")
            n (count digits)]
        (cond
          (zero? n) nil
          (= 1 n) [(parse digits) 0]
          (= 2 n) (if (<= (parse digits) 23)
                    [(parse digits) 0]
                    [(parse (subs digits 0 1)) (* 10 (parse (subs digits 1)))])
          :else [(parse (subs digits 0 (- n 2))) (parse (subs digits (- n 2)))])))))

(defn- parse-typed-time
  "What is typed into the time box as minutes since midnight, or nil while it
   holds nothing that reads as one. 13 to 23 is a 24-hour time and carries its
   own half of the day; anything up to 12 takes the half `pm?` says."
  [text pm?]
  (when-let [[hours minutes] (typed-parts (str/trim (str text)))]
    (let [hours (min 23 hours)
          h24 (cond
                (>= hours 13) hours
                pm? (+ (mod hours 12) 12)
                :else (mod hours 12))]
      (+ (* 60 h24) (min 59 minutes)))))

;; ---------------------------------------------------------------------------
;; The wheel
;; ---------------------------------------------------------------------------

(def ^:private wheel-row-px 32)
(def ^:private wheel-rows 5)

;; A scroll-snapping column with the chosen row held in a band across its
;; middle. The half-column padding that centres the band sits inside its
;; height, so the column is exactly five rows tall.
(o/defstyled wheel-frame :div
  {:position "relative"}
  [:.wheel-band
   {:position "absolute" :left "6px" :right "6px"
    :border-radius "10px"
    :background t/--card-sage-soft-20
    :pointer-events "none"}]
  [:.wheel-column
   {:box-sizing "border-box"
    :overflow-y "auto"
    :scroll-snap-type "y mandatory"
    :scrollbar-width "none"
    :perspective "600px"
    :border-radius "10px"
    :outline "none"}
   ["&::-webkit-scrollbar" {:display "none"}]]
  [:.wheel-row
   {:display "block" :width "100%"
    :padding "0"
    :border "none" :background "transparent"
    :scroll-snap-align "center"
    :transform-origin "center"
    :font-family "inherit" :font-size "19px" :font-weight "500" :line-height "1"
    :color t/--card-forest
    :cursor "pointer"}
   [:&.on {:font-weight "700"}]])

(defn- paint-wheel!
  "The rows fall away from the middle by opacity, a tilt and a shrink, which
   is what makes the column read as a wheel rather than a list."
  [el]
  (let [position (/ (.-scrollTop el) wheel-row-px)]
    (doseq [[j row] (map-indexed vector (array-seq (.-children el)))]
      (let [offset (- j position)
            distance (min 2.6 (js/Math.abs offset))]
        (set! (.. row -style -opacity) (str (max 0.1 (- 1 (* distance 0.42)))))
        (set! (.. row -style -transform)
              (str "rotateX(" (* offset -24) "deg) scale(" (- 1 (* distance 0.09)) ")"))
        (.toggle (.-classList row) "on" (< (js/Math.abs offset) 0.5))))))

(defn- wheel-column
  "`items` on a wheel, turned to `index`. Turning it — scrolling, or the arrow
   keys — picks the row that settles in the band: `on-pick` gets its index
   once the scroll settles, since every frame of a flick is a row nobody
   chose. Pressing a row picks it and calls `on-done`. `loop?` repeats the
   items so the wheel turns past either end."
  [{:keys [items index loop? on-pick on-done]}]
  (r/with-let [!el (atom nil)
               !picked (atom index)
               !settle (atom nil)]
    (let [n (count items)
          rows (if loop? (vec (take (* 5 n) (cycle items))) items)
          last-row (dec (count rows))
          pick! (fn [row]
                  (let [i (mod row n)]
                    (when (not= i @!picked)
                      (reset! !picked i)
                      (on-pick i))))
          middle (* wheel-row-px (quot wheel-rows 2))]
      [wheel-frame {:style {:height (* wheel-row-px wheel-rows)}}
       [:div.wheel-band {:style {:top middle :height wheel-row-px}}]
       [:div.wheel-column
        {:tab-index 0
         :style {:height (* wheel-row-px wheel-rows)
                 :padding (str middle "px 0")}
         :ref (fn [el]
                (when (and el (not @!el))
                  (reset! !el el)
                  (set! (.-scrollTop el)
                        (* wheel-row-px (if loop? (+ index (* 2 n)) index)))
                  (paint-wheel! el)))
         :on-scroll (fn [e]
                      (let [el (.-currentTarget e)
                            span (* n wheel-row-px)]
                        ;; Back to the middle copy before anyone notices.
                        (when loop?
                          (cond
                            (< (.-scrollTop el) span)
                            (set! (.-scrollTop el) (+ (.-scrollTop el) span))

                            (> (.-scrollTop el) (* 3 span))
                            (set! (.-scrollTop el) (- (.-scrollTop el) span))))
                        (paint-wheel! el)
                        (js/clearTimeout @!settle)
                        (reset! !settle
                                (js/setTimeout
                                 (fn []
                                   (when (.-isConnected el)
                                     (pick! (-> (js/Math.round (/ (.-scrollTop el) wheel-row-px))
                                                (max 0)
                                                (min last-row)))))
                                 90))))
         :on-key-down (fn [e]
                        (when-let [step (case (.-key e) "ArrowUp" -1 "ArrowDown" 1 nil)]
                          (.preventDefault e)
                          (let [el (.-currentTarget e)
                                row (+ step (js/Math.round (/ (.-scrollTop el) wheel-row-px)))
                                row (if loop? row (max 0 (min last-row row)))]
                            (.scrollTo el #js {:top (* row wheel-row-px) :behavior "smooth"})
                            (pick! row))))}
        (for [[j item] (map-indexed vector rows)]
          ^{:key j}
          [:button.wheel-row
           {:type "button"
            :tab-index -1
            :style {:height wheel-row-px}
            :on-click (fn [_]
                        (pick! j)
                        (on-done))}
           item])]])
    (finally
      (js/clearTimeout @!settle))))

;; ---------------------------------------------------------------------------
;; The calendar
;; ---------------------------------------------------------------------------

(o/defstyled calendar-frame :div
  {:position "relative"
   :padding "6px 8px 10px"
   :background t/--colour-snow
   :border-width "1px" :border-style "solid" :border-color t/--property-3
   :border-radius "14px"
   :box-shadow "0 1px 3px rgba(45,62,42,0.06)"
   :color t/--card-forest
   :-webkit-tap-highlight-color "transparent"}
  [:.cal-head
   {:display "flex" :align-items "center" :justify-content "space-between"
    :margin-bottom "2px"}]
  [:.cal-nav
   {:width "34px" :height "34px" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "999px"
    :background "transparent"
    :color t/--note-sage
    :cursor "pointer"}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [:.cal-title
   {:display "flex" :gap "4px"}
   [:button
    {:display "flex" :align-items "center"
     :min-height "32px" :padding "4px 8px"
     :border "none" :border-radius "8px"
     :background "transparent"
     :font-family "inherit" :font-size "15px" :font-weight "600"
     :color t/--card-forest
     :cursor "pointer"}
    [:&.on {:background t/--card-sage-soft-20}]]]
  [:.cal-weekdays
   {:display "grid" :grid-template-columns "repeat(7, 1fr)"
    :margin-bottom "2px"}
   [:span
    {:padding "3px 0"
     :text-align "center"
     :font-size "11px" :font-weight "600"
     :color t/--link-muted}]]
  [:.cal-days
   {:display "grid" :grid-template-columns "repeat(7, 1fr)" :row-gap "2px"}]
  [:.cal-cell
   {:height "38px"
    :display "flex" :align-items "center" :justify-content "center"}]
  [:.cal-out {:font-size "14px" :color t/--card-sage-light}]
  [:.cal-day
   {:width "34px" :height "34px" :padding "0"
    :border-width "1.5px" :border-style "solid" :border-color "transparent"
    :border-radius "999px"
    :background "transparent"
    :font-family "inherit" :font-size "14px" :font-weight "500"
    :color t/--card-forest
    :cursor "pointer"
    :transition "background 0.12s, color 0.12s"}
   [:&.past {:color t/--link-muted}]
   [:&.today {:border-color t/--card-sage :font-weight "700"}]
   [:&.picked {:background t/--card-forest :color "white" :font-weight "700"}]
   [:&.picked.today
    {:box-shadow "0 0 0 2px var(--colour-snow), 0 0 0 3.5px var(--card-sage)"}]]
  ;; Hover cues only where hover exists.
  [:at-media {:hover "hover"}
   [".cal-nav:hover" {:background t/--card-forest-06}]
   [".cal-title button:not(.on):hover" {:background t/--card-forest-06}]
   [".cal-day:not(.picked):hover" {:background t/--card-forest-06}]]
  ;; The month or year wheel, over the calendar it changes.
  [:.cal-wheel-cover
   {:position "absolute" :inset "0" :z-index "3"
    :display "flex" :align-items "flex-start" :justify-content "center"
    :padding-top "66px"
    :border-radius "14px"
    :background "rgba(253,254,252,0.78)"}]
  [:.cal-wheel
   {:padding "8px 10px 10px"
    :border-radius "14px"
    :background t/--colour-snow
    :box-shadow "0 8px 28px rgba(45,62,42,0.14)"}
   [:&.month {:width "64%"}]
   [:&.year {:width "50%"}]])

(def ^:private year-span 7)

(defn calendar
  "A month of days, from Monday, to pick one from — `value`'s month or this
   one to begin with. The chevrons page the months, and the month's and the
   year's names open a wheel over the calendar to turn to another. Today is
   ringed and the past muted. Picking the picked day again unpicks it:
   `on-pick` gets the day, or nil."
  [{:keys [value on-pick]}]
  (r/with-let [!view (r/atom (select-keys (or value (today-date)) [:year :month]))
               !wheel (r/atom nil)]
    (let [{:keys [year month] :as view} @!view
          wheel @!wheel
          today (today-date)
          first-year (dec (:year today))
          toggle-wheel! (fn [kind] (swap! !wheel #(when-not (= % kind) kind)))
          close-wheel! #(reset! !wheel nil)]
      [calendar-frame
       [:div.cal-head
        [:button.cal-nav {:type "button"
                          :aria-label "Previous month"
                          :on-click #(swap! !view shift-month -1)}
         [previous-icon]]
        [:span.cal-title
         [:button {:type "button"
                   :class (when (= :month wheel) "on")
                   :on-click #(toggle-wheel! :month)}
          (nth month-names (dec month))]
         [:button {:type "button"
                   :class (when (= :year wheel) "on")
                   :on-click #(toggle-wheel! :year)}
          year]]
        [:button.cal-nav {:type "button"
                          :aria-label "Next month"
                          :on-click #(swap! !view shift-month 1)}
         [next-icon]]]
       [:div.cal-weekdays
        (for [initial weekday-initials]
          ^{:key initial} [:span initial])]
       [:div.cal-days
        (for [[idx {:keys [day adjacent?]}] (map-indexed vector (month-cells view))]
          (if adjacent?
            ^{:key idx}
            [:div.cal-cell [:span.cal-out day]]
            (let [date (assoc view :day day)
                  picked? (same-date? value date)
                  today? (same-date? today date)]
              ^{:key idx}
              [:div.cal-cell
               [:button.cal-day
                {:type "button"
                 :class (cond-> []
                          picked? (conj "picked")
                          today? (conj "today")
                          (before? date today) (conj "past"))
                 :aria-label (str (day-label date) (when today? ", today"))
                 :aria-pressed picked?
                 :on-click #(on-pick (when-not picked? date))}
                day]])))]
       (when wheel
         [:div.cal-wheel-cover {:on-click close-wheel!}
          [:div.cal-wheel {:class (name wheel)
                           :on-click #(.stopPropagation %)}
           (case wheel
             :month [wheel-column {:items month-names
                                   :index (dec month)
                                   :loop? true
                                   :on-pick #(swap! !view assoc :month (inc %))
                                   :on-done close-wheel!}]
             :year [wheel-column {:items (mapv str (range first-year (+ first-year year-span)))
                                  :index (-> (- year first-year) (max 0) (min (dec year-span)))
                                  :on-pick #(swap! !view assoc :year (+ first-year %))
                                  :on-done close-wheel!}])]])])))

;; ---------------------------------------------------------------------------
;; The time
;; ---------------------------------------------------------------------------

(o/defstyled time-frame :div
  {:display "flex" :flex-direction "column" :align-items "center" :gap "12px"
   :padding "12px"
   :background t/--colour-snow
   :border-width "1px" :border-style "solid" :border-color t/--property-3
   :border-radius "14px"
   :box-shadow "0 1px 3px rgba(45,62,42,0.06)"
   :color t/--card-forest
   :-webkit-tap-highlight-color "transparent"}
  [:.time-typed
   {:display "flex" :align-items "center" :gap "8px"}]
  ;; One box round one value, typed into like any other field.
  [:.time-box
   {:width "112px" :min-height "50px"
    :box-sizing "border-box"
    :padding "0 8px"
    :text-align "center"
    :border-width "1.5px" :border-style "solid" :border-color t/--property-3
    :border-radius "12px"
    :background "#FFFFFF"
    :outline "none"
    :font-family "inherit" :font-size "25px" :font-weight "700"
    :letter-spacing "0.01em"
    :color t/--card-forest}
   [:&:focus {:border-color t/--card-forest
              :box-shadow "0 0 0 3px rgba(156,174,133,0.25)"}]]
  ;; AM and PM side by side: a choice, not a read-out.
  [:.time-halves
   {:display "inline-flex" :gap "2px"
    :padding "2px"
    :border-radius "10px"
    :background t/--card-forest-06}
   [:button
    {:min-width "46px" :min-height "38px"
     :padding "0 10px"
     :border "none" :border-radius "8px"
     :background "transparent"
     :font-family "inherit" :font-size "12.5px" :font-weight "700"
     :letter-spacing "0.04em"
     :color t/--link-muted
     :cursor "pointer"}
    [:&.on {:background t/--card-forest :color "white"}]]]
  [:.time-quick
   {:display "grid" :grid-template-columns "repeat(3, 1fr)" :gap "6px"
    :width "100%"}]
  [:.time-quick-pick
   {:display "flex" :flex-direction "column" :align-items "center" :justify-content "center"
    :gap "1px"
    :min-height "48px" :padding "0 6px"
    :border-width "1.5px" :border-style "solid" :border-color "transparent"
    :border-radius "11px"
    :background t/--card-forest-06
    :font-family "inherit"
    :cursor "pointer"
    :transition "background 0.12s, border-color 0.12s"}
   [:b {:font-size "13px" :font-weight "600" :color t/--card-forest}]
   [:i {:font-size "11px" :font-style "normal" :font-weight "500"
        :color t/--link-muted :white-space "nowrap"}]
   [:&.on
    {:background t/--card-sage-soft-20 :border-color t/--card-forest}
    [:b {:font-weight "800"}]
    [:i {:color t/--card-forest}]]]
  [:at-media {:hover "hover"}
   [".time-halves button:not(.on):hover" {:color t/--card-forest}]
   [".time-quick-pick:not(.on):hover" {:border-color t/--property-3}]])

(defn time-view
  "A time of day typed into one box — read on every keystroke, left exactly as
   typed until you leave it, then tidied — beside an AM/PM toggle, over three
   quick picks. `on-change` gets minutes since midnight, or nil when the picked
   quick pick is pressed again. `auto-focus?` puts the caret in the box with
   its text selected — without scrolling to it, since the view may still be
   sliding into place."
  [{:keys [value on-change auto-focus?]}]
  (r/with-let [!typed (r/atom nil)
               focus-box! (fn [el]
                            (when (and el auto-focus?)
                              (.focus el #js {:preventScroll true})))]
    (let [shown (or value default-time)
          pm? (>= shown (* 12 60))]
      [time-frame
       [:div.time-typed
        [:input.time-box
         {:type "text"
          :input-mode "numeric"
          :auto-complete "off"
          :spell-check false
          :aria-label "Time"
          :ref focus-box!
          :value (or @!typed (typed-label shown))
          :on-focus #(.. % -target select)
          :on-change (fn [e]
                       (let [text (.. e -target -value)]
                         (reset! !typed text)
                         (when-let [minutes (parse-typed-time text pm?)]
                           (on-change minutes))))
          :on-blur #(reset! !typed nil)}]
        [:div.time-halves
         (for [[label want-pm?] [["AM" false] ["PM" true]]]
           ^{:key label}
           [:button {:type "button"
                     :class (when (= want-pm? pm?) "on")
                     :aria-pressed (= want-pm? pm?)
                     ;; Moves the time by twelve hours and nothing else, and
                     ;; leaves the caret where it was.
                     :on-mouse-down #(.preventDefault %)
                     :on-click #(on-change (cond
                                             (= want-pm? pm?) shown
                                             want-pm? (+ shown (* 12 60))
                                             :else (- shown (* 12 60))))}
            label])]]
       [:div.time-quick
        (for [[label minutes] quick-times]
          ^{:key label}
          [:button.time-quick-pick
           {:type "button"
            :class (when (= value minutes) "on")
            :aria-pressed (= value minutes)
            :on-click #(on-change (when-not (= value minutes) minutes))}
           [:b label]
           [:i (format-minutes minutes)]])]])))
